import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';

const PRIMARY_ROUTES = ['/', '/important-dates', '/properties'];
const PDF_TOOLS_URL = 'https://app.bpasolutions.au/SafePDF-local';

const FALLBACK_ITEMS = {
  '/': { path: '/', label: 'Dashboard', icon: 'Grid' },
  '/important-dates': { path: '/important-dates', label: 'Important Dates', icon: 'Calendar' },
  '/properties': { path: '/properties', label: 'Properties', icon: 'Home' },
};

const flattenItems = (items) => {
  const result = [];
  const seenPaths = new Set();
  const walk = (list) => {
    if (!Array.isArray(list)) return;
    for (const item of list) {
      if (!item) continue;
      if (item.path && item.label) {
        if (!seenPaths.has(item.path)) {
          seenPaths.add(item.path);
          result.push({
            path: item.path,
            label: item.label,
            icon: item.icon,
          });
        }
      }
      if (Array.isArray(item.children)) walk(item.children);
      if (Array.isArray(item.nestedItems)) walk(item.nestedItems);
      if (Array.isArray(item.items)) walk(item.items);
    }
  };
  walk(items);
  return result;
};

const isRouteActive = (pathname, route) => {
  if (route === '/') return pathname === '/';
  return pathname === route || pathname.startsWith(route + '/');
};

const MobileBottomNavigation = ({ items = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const flatItems = useMemo(() => flattenItems(items), [items]);

  const primaryItems = useMemo(() => {
    return PRIMARY_ROUTES.map((route) => {
      const found = flatItems.find((it) => it.path === route);
      if (found) return found;
      return FALLBACK_ITEMS[route] || { path: route, label: route, icon: 'Circle' };
    });
  }, [flatItems]);

  const moreItems = useMemo(() => {
    return flatItems.filter((it) => !PRIMARY_ROUTES.includes(it.path));
  }, [flatItems]);

  const moreIsActive = useMemo(() => {
    return moreItems.some((it) => isRouteActive(location.pathname, it.path));
  }, [moreItems, location.pathname]);

  useEffect(() => {
    if (isMoreOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isMoreOpen]);

  useEffect(() => {
    if (!isMoreOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsMoreOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMoreOpen]);

  const handlePrimaryClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const handleSmartSearch = useCallback(() => {
    setIsMoreOpen(false);
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));
  }, []);

  const handleMoreClick = useCallback(() => {
    setIsMoreOpen(true);
  }, []);

  const handleMoreItemSelect = useCallback((path) => {
    setIsMoreOpen(false);
    navigate(path);
  }, [navigate]);

  const handleCloseMore = useCallback(() => {
    setIsMoreOpen(false);
  }, []);

  const visibleStyle = { opacity: 1, visibility: 'visible' };

  return (
    <>
      <nav
        aria-label="Mobile navigation"
        className="xl:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200/90 bg-white/98 shadow-[0_-1px_10px_rgba(15,23,42,0.08)] backdrop-blur-md"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto grid w-full max-w-[820px] grid-cols-5 px-1 sm:px-4" style={{ minHeight: '54px' }}>
          {primaryItems.map((item) => {
            const active = isRouteActive(location.pathname, item.path);
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => handlePrimaryClick(item.path)}
                aria-label={item.label}
                className="relative flex min-h-[44px] min-w-0 flex-col items-center justify-center gap-0 px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-inset transition-colors"
                style={visibleStyle}
              >
                <span aria-hidden="true" className={`absolute left-1/2 top-0 h-[2px] w-8 -translate-x-1/2 rounded-full transition-opacity ${active ? 'bg-amber-500 opacity-100' : 'opacity-0'}`} />
                <span
                  className={`flex min-h-[29px] min-w-[32px] items-center justify-center transition-colors ${active ? 'text-amber-600' : 'text-slate-500'}`}
                >
                  <SafeIcon icon={item.icon} className="text-[19px] sm:text-xl" />
                </span>
                <span
                  className={`max-w-full truncate text-[9px] font-medium leading-[1.05] transition-colors sm:text-[10px] ${active ? 'text-amber-700' : 'text-slate-500'}`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleSmartSearch}
            aria-label="Open Smart Search"
            className="relative flex min-h-[44px] min-w-0 flex-col items-center justify-center gap-0 px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-inset transition-colors"
            style={visibleStyle}
          >
            <span className="flex min-h-[29px] min-w-[32px] items-center justify-center text-slate-500 transition-colors">
              <SafeIcon icon="Search" className="text-[19px] sm:text-xl" />
            </span>
            <span className="max-w-full truncate text-[9px] font-medium leading-[1.05] text-slate-500 transition-colors sm:text-[10px]">
              Search
            </span>
          </button>
          <button
            type="button"
            onClick={handleMoreClick}
            aria-label="More navigation options"
            className="relative flex min-h-[44px] min-w-0 flex-col items-center justify-center gap-0 px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-inset transition-colors"
            style={visibleStyle}
          >
            <span aria-hidden="true" className={`absolute left-1/2 top-0 h-[2px] w-8 -translate-x-1/2 rounded-full transition-opacity ${isMoreOpen || moreIsActive ? 'bg-amber-500 opacity-100' : 'opacity-0'}`} />
            <span
              className={`flex min-h-[29px] min-w-[32px] items-center justify-center transition-colors ${isMoreOpen || moreIsActive ? 'text-amber-600' : 'text-slate-500'}`}
            >
              <span className="select-none text-[20px] leading-none sm:text-[21px]">⋯</span>
            </span>
            <span
              className={`max-w-full truncate text-[9px] font-medium leading-[1.05] transition-colors sm:text-[10px] ${isMoreOpen || moreIsActive ? 'text-amber-700' : 'text-slate-500'}`}
            >
              More
            </span>
          </button>
        </div>
      </nav>

      {isMoreOpen && (
        <div className="xl:hidden fixed inset-0 z-50 flex items-end justify-center">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
            onClick={handleCloseMore}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="More navigation"
            className="relative flex w-full flex-col rounded-t-2xl bg-white shadow-2xl animate-fade-in-up sm:mx-4 sm:max-w-xl"
            style={{
              maxHeight: 'min(75vh,680px)',
              paddingBottom: 'max(12px,env(safe-area-inset-bottom))',
            }}
          >
            <div className="flex justify-center pt-3 pb-1" aria-hidden="true">
              <div className="w-10 h-1.5 bg-slate-300 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-900">More</h2>
              <button
                type="button"
                onClick={handleCloseMore}
                aria-label="Close more navigation"
                className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
              >
                <SafeIcon icon="X" className="text-lg" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-3 py-2">
              {moreItems.length > 0 ? (
                <ul className="space-y-1">
                  {moreItems.map((item) => {
                    const active = isRouteActive(location.pathname, item.path);
                    return (
                      <li key={item.path}>
                        <button
                          type="button"
                          onClick={() => handleMoreItemSelect(item.path)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${active ? 'bg-amber-50 text-amber-700' : 'text-slate-700 hover:bg-slate-50'}`}
                        >
                          <SafeIcon icon={item.icon} className="text-lg flex-shrink-0" />
                          <span>{item.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="px-4 py-8 text-center text-sm text-slate-400">
                  No additional navigation items.
                </div>
              )}
            </div>

            <div className="px-3 pt-2 pb-1 border-t border-slate-100">
              <a
                href={PDF_TOOLS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCloseMore}
                aria-label="Open PDF Tools in a new tab"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-amber-700 hover:bg-amber-50 active:bg-amber-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
              >
                <SafeIcon
                  icon="FileText"
                  className="text-lg flex-shrink-0"
                />

                <span className="flex-1 min-w-0 text-left">
                  <span className="block">PDF Tools</span>
                  <span className="block text-xs font-normal text-slate-500">
                    Compress, split or optimise PDFs
                  </span>
                </span>

                <SafeIcon
                  icon="ExternalLink"
                  className="text-sm flex-shrink-0"
                />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomNavigation;